import { gql } from '@apollo/client';
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { t } from 'i18next';
import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Trans } from 'react-i18next';
import { z } from 'zod';
import { InfoAlert } from '~components/ui/Alert';
import { ModalCloseButton } from '~components/ui/ModalCloseButton';
import { fromError } from '~utils/errors';
import { useNotificationEmailUpdateMutation } from './__generated__/assignUserModel.graphql';

const schema = z.object({
  email1: z.string().email(t('invalidEmail')).optional().or(z.literal('')),
  email2: z.string().email(t('invalidEmail')).optional().or(z.literal('')),
  email3: z.string().email(t('invalidEmail')).optional().or(z.literal('')),
});

type FormData = z.infer<typeof schema>;

interface Props {
  isOpen: boolean;
  onClose: () => void;
  id: string;
  customerName: string;
  emailAddresses: string[];
}

export function AssignUsersModal({ isOpen, onClose, id, emailAddresses }: Props) {
  const toast = useToast();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      email1: emailAddresses[0] || '',
      email2: emailAddresses[1] || '',
      email3: emailAddresses[2] || '',
    },
  });
  const resetEmails = useCallback(() => {
    reset({
      email1: emailAddresses[0] || '',
      email2: emailAddresses[1] || '',
      email3: emailAddresses[2] || '',
    });
  }, [emailAddresses, reset]);

  useEffect(() => {
    resetEmails();
  }, [resetEmails]);
  const [updateNotificationSettings] = useNotificationEmailUpdateMutation({
    refetchQueries: ['EmailNotificationsPage'],
    awaitRefetchQueries: true,
  });
  const handleClose = useCallback(() => {
    resetEmails();
    onClose();
  }, [resetEmails, onClose]);
  const handleUpdate = useCallback(
    async (data: FormData) => {
      const updatedEmails = [data.email1, data.email2, data.email3].filter(
        (e): e is string => typeof e === 'string' && e.trim() !== '',
      );

      try {
        const { data: result } = await updateNotificationSettings({
          variables: {
            input: {
              id,
              emailAddresses: updatedEmails,
            },
          },
        });

        if (!result?.notificationEmailUpdate) {
          throw new Error(t('notificationUpdateFailed'));
        }

        // toast({
        //   status: 'success',
        //   title: t('usersAssignedSuccessTitle', { customerName }),
        //   description: t('usersAssignedSuccessDesc', { customerName }),
        // });
        handleClose();
      } catch (err) {
        toast({
          status: 'error',
          title: t('updateNotificationError'),
          description: fromError(err, 'NotificationSettingsUpdate'),
        });
      }
    },
    [updateNotificationSettings, id, handleClose, toast],
  );

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg" isCentered>
      <ModalOverlay />
      <form onSubmit={handleSubmit(handleUpdate)}>
        <ModalContent>
          <ModalHeader>{t('manageRecipients')}</ModalHeader>
          <ModalCloseButton onClick={handleClose} />
          <ModalBody>
            <InfoAlert mb={8}>
              <Trans i18nKey="assignUsersInfo" components={{ strong: <strong /> }} />
            </InfoAlert>
            <Text mb={8}>{t('addUsersEnc')}</Text>
            <VStack align="stretch" spacing={8}>
              <FormControl isInvalid={!!errors.email1}>
                <FormLabel>{t('email1')}</FormLabel>
                <Input
                  placeholder={t('enterEmail')}
                  autoComplete="email1"
                  {...register('email1')}
                />
                <FormErrorMessage>{errors.email1?.message}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.email2}>
                <FormLabel>{t('email2')}</FormLabel>
                <Input
                  placeholder={t('enterEmail')}
                  autoComplete="email2"
                  {...register('email2')}
                />
                <FormErrorMessage>{errors.email2?.message}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.email3}>
                <FormLabel>{t('email3')}</FormLabel>
                <Input
                  placeholder={t('enterEmail')}
                  autoComplete="email3"
                  {...register('email3')}
                />
                <FormErrorMessage>{errors.email3?.message}</FormErrorMessage>
              </FormControl>

              {errors?.root && (
                <Box color="red.500" fontSize="sm">
                  {errors.root.message}
                </Box>
              )}
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" onClick={handleClose} isDisabled={isSubmitting}>
              {t('Cancel')}
            </Button>
            <Button type="submit" colorScheme="blue" ml={3} isLoading={isSubmitting}>
              {t('Save')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </form>
    </Modal>
  );
}

AssignUsersModal.graphql = {
  mutations: {
    notificationEmailUpdate: gql`
      mutation notificationEmailUpdate($input: NotificationEmailInput!) {
        notificationEmailUpdate(input: $input) {
          id
          status
          emailAddresses
        }
      }
    `,
  },
};
